import React, { useState, useEffect } from 'react'
import { Bell, Menu, Search, Settings, User, LogOut, Shield, ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import AuthAPI from '../../services/auth'
import SearchInput from '../Common/SearchInput'
import GlobalSearchModal from '../Common/GlobalSearchModal'

const Header = ({ onMenuClick, sidebarOpen }) => {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showGlobalSearch, setShowGlobalSearch] = useState(false)
  const [globalSearchTerm, setGlobalSearchTerm] = useState('')

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await AuthAPI.getCurrentUser()
        setUser(userData)
        // Check if user is super admin
        setIsAdmin(userData?.email === 'nigelcannings@gmail.com')
      } catch (error) {
        console.error('Failed to load user data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadUserData()
  }, [])

  const handleLogout = async () => {
    try {
      await AuthAPI.logout()
      // Use window.location to bypass ProtectedRoute redirect
      window.location.href = '/'
    } catch (error) {
      console.error('Logout error:', error)
      // Use window.location to bypass ProtectedRoute redirect
      window.location.href = '/'
    }
  }

  const handleSettingsClick = () => {
    navigate('/app/settings')
  }

  const handleGlobalSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      setGlobalSearchTerm(searchTerm)
      setShowGlobalSearch(true)
    }
  }

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleGlobalSearch(e.target.value)
    }
  }

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showUserMenu])

  return (
    <header className="bg-white border-b border-secondary-200 px-4 py-3 lg:px-6">
      <div className="flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Search bar */}
          <div className="hidden sm:block">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search tickets, activities, settings..."
                onKeyPress={handleSearchKeyPress}
                className="pl-10 pr-4 py-2 border border-secondary-300 rounded-lg bg-secondary-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-64 cursor-pointer hover:bg-white transition-colors"
                onClick={() => setShowGlobalSearch(true)}
                readOnly
              />
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-2">
          {/* Search button (mobile) */}
          <button 
            onClick={() => setShowGlobalSearch(true)}
            className="sm:hidden p-2 rounded-lg text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 transition-colors"
            title="Search"
          >
            <Search className="w-5 h-5" />
          </button>

          {/* Admin Indicator */}
          {isAdmin && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-lg text-xs font-medium">
              <Shield className="w-3 h-3" />
              <span>Admin</span>
            </div>
          )}

          {/* Notifications */}
          <button className="relative p-2 rounded-lg text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 transition-colors">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>

          {/* Settings */}
          <button 
            onClick={handleSettingsClick}
            className="p-2 rounded-lg text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 transition-colors"
            title="Settings"
          >
            <Settings className="w-5 h-5" />
          </button>

          {/* User menu */}
          <div className="relative user-menu">
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 p-2 rounded-lg text-secondary-700 hover:bg-secondary-100 transition-colors"
            >
              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                {isAdmin ? (
                  <Shield className="w-4 h-4 text-white" />
                ) : (
                  <User className="w-4 h-4 text-white" />
                )}
              </div>
              <span className="hidden md:block text-sm font-medium">
                {loading ? 'Loading...' : (user?.name || user?.email?.split('@')[0] || 'User')}
              </span>
              <ChevronDown className={`w-4 h-4 text-secondary-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-secondary-200 rounded-lg shadow-lg py-2 z-50">
                {/* User Info */}
                <div className="px-4 py-3 border-b border-secondary-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                      {isAdmin ? (
                        <Shield className="w-5 h-5 text-white" />
                      ) : (
                        <User className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-secondary-900 flex items-center space-x-2">
                        <span>{user?.name || 'User'}</span>
                        {isAdmin && (
                          <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-secondary-500">{user?.email}</div>
                    </div>
                  </div>
                </div>
                
                {/* Menu Items */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      navigate('/app/settings')
                      setShowUserMenu(false)
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Account Settings</span>
                  </button>
                  
                  {isAdmin && (
                    <button
                      onClick={() => {
                        navigate('/app/admin')
                        setShowUserMenu(false)
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-orange-700 hover:bg-orange-50 transition-colors"
                    >
                      <Shield className="w-4 h-4" />
                      <span>Admin Dashboard</span>
                    </button>
                  )}
                  
                  <div className="border-t border-secondary-100 mt-2 pt-2">
                    <button
                      onClick={() => {
                        handleLogout()
                        setShowUserMenu(false)
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile search bar */}
      <div className="sm:hidden mt-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input
            type="text"
            placeholder="Search tickets, activities, settings..."
            onClick={() => setShowGlobalSearch(true)}
            className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg bg-secondary-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer hover:bg-white transition-colors"
            readOnly
          />
        </div>
      </div>

      {/* Global Search Modal */}
      <GlobalSearchModal
        isOpen={showGlobalSearch}
        onClose={() => setShowGlobalSearch(false)}
        initialQuery={globalSearchTerm}
      />
    </header>
  )
}

export default Header